import React from "react";
import { useQuery } from "@tanstack/react-query";
import { MessageSquareText, Camera, Video } from "lucide-react";
import { API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Stars } from "@/components/Stars";

const fetchLatest = async () => {
  const r = await fetch(`${API}/reviews/latest?limit=3`);
  if (!r.ok) throw new Error("latest failed");
  return r.json();
};

const UNITS = [["year", 31536000], ["month", 2592000], ["week", 604800], ["day", 86400], ["hour", 3600], ["minute", 60]];

export function timeAgo(iso, lang) {
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(lang === "ar" ? "ar-EG-u-nu-latn" : lang, { numeric: "auto" });
  for (const [unit, s] of UNITS) {
    if (Math.abs(sec) >= s) return rtf.format(-Math.round(sec / s), unit);
  }
  return rtf.format(0, "second");
}

function ReviewCard({ r, country, lang, onSelect }) {
  return (
    <li>
      <button onClick={() => onSelect(r.country_code)} data-testid={`latest-review-${r.id}`}
        className="w-full h-full text-start rounded-2xl border border-stoneLine bg-card hover:bg-sandDeep/40 transition-colors p-3.5 flex flex-col gap-2">
        <span className="flex items-center gap-2 min-w-0">
          {r.author.picture ? (
            <img src={r.author.picture} alt="" className="w-7 h-7 rounded-full shrink-0" referrerPolicy="no-referrer" />
          ) : (
            <span className="w-7 h-7 rounded-full bg-sandDeep shrink-0 flex items-center justify-center text-[11px] font-bold">{r.author.name?.[0]}</span>
          )}
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-ink truncate">{r.author.name}</span>
            <span className="block text-[10px] text-muted-foreground" data-testid={`latest-time-${r.id}`}>{timeAgo(r.created_at, lang)}</span>
          </span>
          <Stars value={r.rating} size="w-3 h-3" />
        </span>
        <span className="text-sm text-ink/85 leading-snug line-clamp-2 flex-1" dir="auto">{r.text}</span>
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {country && <img src={country.flag_url} alt="" className="w-5 h-3.5 object-cover rounded-sm border border-stoneLine" />}
          <span className="font-medium text-ink truncate">{country ? country.short_names[lang] : r.country_code}</span>
          {r.photo_url && <Camera className="w-3.5 h-3.5 ms-auto text-ochre" />}
          {r.video_url && <Video className="w-3.5 h-3.5 ms-auto text-ochre" />}
        </span>
      </button>
    </li>
  );
}

/** Home strip: the 3 newest approved comments across the atlas, refreshed every 60 s. */
export default function LatestReviews({ countries, onSelect }) {
  const { lang, t } = useLang();
  const { data } = useQuery({ queryKey: ["latest-reviews"], queryFn: fetchLatest, refetchInterval: 60_000, refetchOnWindowFocus: true });
  const reviews = data?.reviews || [];
  if (reviews.length === 0) return null;

  return (
    <div className="mt-6" data-testid="latest-reviews">
      <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-muted-foreground">
        <MessageSquareText className="w-3 h-3 text-ochre" /> {t("latest_reviews")}
        <span className="ms-auto normal-case tracking-normal flex items-center gap-1.5 text-[10px]">
          <span className="relative flex w-2 h-2"><span className="absolute inset-0 rounded-full bg-oasis animate-ping opacity-60" /><span className="relative w-2 h-2 rounded-full bg-oasis" /></span>
          {t("live")}
        </span>
      </div>
      <ul className="grid gap-2 sm:grid-cols-3">
        {reviews.map((r) => <ReviewCard key={r.id} r={r} lang={lang} onSelect={onSelect} country={countries.find((c) => c.code === r.country_code)} />)}
      </ul>
    </div>
  );
}
